import type { InvestigationReportData } from '../types';

/**
 * Normalize LLM / API report payloads to the OpenSRE InvestigationReportData shape.
 * Handles camelCase aliases and common type mismatches.
 */
export function normalizeInvestigationReport(
  raw: InvestigationReportData & Record<string, unknown>
): InvestigationReportData {
  const r: InvestigationReportData & Record<string, any> = { ...raw };

  if (!r.root_cause && r.rootCause) r.root_cause = r.rootCause as InvestigationReportData['root_cause'];
  if (!r.root_cause && r.rootCauseAnalysis) {
    r.root_cause = r.rootCauseAnalysis as InvestigationReportData['root_cause'];
  }
    if (!r.action_items && r.actionItems) {
      r.action_items = r.actionItems as InvestigationReportData['action_items'];
    }
    if (!r.action_items && r.recommended_next_steps) {
      r.action_items = r.recommended_next_steps as InvestigationReportData['action_items'];
    }
    if (!r.action_items && r.recommendedNextSteps) {
      r.action_items = r.recommendedNextSteps as InvestigationReportData['action_items'];
    }
    if (!r.action_items && r.next_steps) {
      r.action_items = r.next_steps as InvestigationReportData['action_items'];
    }
    if (!r.action_items && r.nextSteps) {
      r.action_items = r.nextSteps as InvestigationReportData['action_items'];
    }
    if (!r.action_items && r.recommendations) {
      r.action_items = r.recommendations as InvestigationReportData['action_items'];
    }
  if (!r.lessons_learned && r.lessonsLearned) {
    r.lessons_learned = r.lessonsLearned as string[];
  }
  if (!r.affected_services && r.affectedServices) {
    r.affected_services = r.affectedServices as string[];
  }
  if (!r.executive_summary && r.executiveSummary) {
    r.executive_summary = r.executiveSummary as string;
  }
  if (!r.status && r.resolutionStatus) r.status = r.resolutionStatus as string;

  if (!r.affected_services) {
    r.affected_services = (r.services || r.services_affected) as string[] | undefined;
  }
  if (!r.status) r.status = r.resolution_status as string | undefined;
  if (!r.root_cause && r.root_cause_analysis) {
    r.root_cause = r.root_cause_analysis as InvestigationReportData['root_cause'];
  }

  if (r.executive_summary && typeof r.executive_summary === 'object') {
    r.executive_summary = Object.values(r.executive_summary as object)
      .filter(Boolean)
      .join(' ');
  }

  const rc: unknown = r.root_cause;
  if (typeof rc === 'string') {
    r.root_cause = { summary: rc, confidence: 'probable' };
  } else if (rc && typeof rc === 'object') {
    const obj = rc as Record<string, unknown>;
    r.root_cause = {
      summary: String(
        obj.summary ||
          obj.analysis ||
          obj.description ||
          obj.detail ||
          obj.primary_root_cause ||
          JSON.stringify(obj)
      ),
      confidence: (obj.confidence as string) || 'probable',
      details: (obj.details as string) || null,
    };
  }

  if (Array.isArray(r.action_items)) {
    r.action_items = r.action_items.map((item: any) => {
      if (typeof item === 'string') return { priority: 'short_term', action: item };
      return {
        priority: String(item.priority || item.urgency || 'short_term'),
        action: String(
          item.action ||
            item.description ||
            item.recommendation ||
            item.next_step ||
            item.nextStep ||
            item.task ||
            ''
        ),
      };
    });
  }

  if (Array.isArray(r.lessons_learned)) {
    const flat: string[] = [];
    for (const item of r.lessons_learned as unknown[]) {
      if (typeof item === 'string') flat.push(item);
      else if (item && typeof item === 'object') {
        const o = item as Record<string, unknown>;
        flat.push(String(o.lesson || o.text || o.summary || JSON.stringify(item)));
      }
    }
    r.lessons_learned = flat;
  }

  if (!r.title) r.title = 'Investigation Report';

  return r;
}
